<template>
  <div id="app" class="app-background">
      <router-view />

      <CreateUserModal :showModal="showCreateUserModal" @close="closeCreateUserModal" @refresh="fetchUsers" />
      <CreateProjectModal class="mb-4"
        ref="createProjectModal"
        :showModal="showCreateProjectModal" 
        @close="closeCreateProjectModal"       
        @projectCreated="handleProjectCreated" 
        @refresh="fetchProjects" 
      />
      <AddProjectMemberModal class="mb-4"
        ref="addProjectMemberModal"
        :showModal="showAddProjectMemberModal" 
        @close="closeAddProjectMemberModal"       
        @member-added="handleMemberAdded" 
        @refresh="fetchProjectMembers" 
        :projectId="selectedProjectId"
        :projectName="selectedProjectName"
      />
      <EditTalentModal
        v-if="showEditModal"
        :selectedTalent="selectedTalent"
        @save="submitEditModal"
        @close="closeEditTalentModal"
        ref="editTalentModal"
      />
      <ProgressReportModal class="mb-4"
        ref="progressReportsModal"
        :showModal="showProgressReportsModal" 
        @close="closeProgressReportModal"       
        @reportSubmitted="handleProjectCreated" 
        @refresh="fetchProgressReports" 
      />
      <AssignRoleModal class="mb-4"
        ref="AssignRoleModal"
        :showModal="showAssignRoleModal" 
        @close="closeAssignRoleModal"       
      />
  </div>
</template>

<script>
import { computed } from "vue";
import { useRoute } from "vue-router";
import CreateUserModal from "@/components/modals/CreateUserModal.vue";
import CreateProjectModal from "@/components/modals/CreateProjectModal.vue";
import EditTalentModal from "@/components/modals/EditTalentModal.vue";
import eventBus from "@/eventBus";
import apiClient from "@/axios";
import AddProjectMemberModal from "@/components/modals/AddProjectMemberModal.vue";
import ProgressReportModal from "@/components/modals/ProgressReportModal.vue";
import AssignRoleModal from "@/components/modals/AssignRoleModal.vue";

export default {
  name: "App",
  components: {
    CreateUserModal,
    EditTalentModal,
    CreateProjectModal,
    AddProjectMemberModal,
    ProgressReportModal,
    AssignRoleModal,
  },
  data() {
    return {
      selectedProjectId: null,
      selectedProjectName: null,
      showCreateUserModal: false,
      showCreateProjectModal: false,
      showCreateTaskModal: false,
      showAddProjectMemberModal: false,
      showProgressReportsModal: false,
      showAssignRoleModal: false,

      showEditModal: false,
      selectedTalent: {
        user: {
          first_name: "",
          last_name: "",
          email: "",
        },
      },
      availableSkills: [],
      selectedSkill: "",
    };
  },

  computed: {
    isAdmin() {
      return this.$store.getters.isAdmin;
    },
    isAuthenticated() {
      return this.$store.state.token && this.$store.state.user;
    },
  },
  watch: {
    isAuthenticated: {
      immediate: true,
      handler(newVal) {
        if (!newVal && this.$route.meta.requiresAuth) {
          this.$router.replace({ name: "Login" });
        }
      },
    },
    '$route': {
      immediate: true,
      handler(to) {
        if (!to.meta.requiresAuth) return;
        
        const token = localStorage.getItem('token');
        const user = JSON.parse(localStorage.getItem('user') || 'null');
        
        if (!token || !user) {
          this.$store.commit('logout');
          this.$router.push({ name: 'Login' });
        }
      }
    }
  },

  setup() {
    const route = useRoute();
    const showSidebar = computed(
      () =>
        ![
          "Login",
          "NotFound",
          "ForgotPassword",
        ].includes(route.name)
    );
    return { showSidebar };
  },
  methods: {
    openCreateUserModal() {
      this.showCreateUserModal = true;
    },
    closeCreateUserModal() {
      this.showCreateUserModal = false;
    },

    openCreateProjectModal() {
      this.showCreateProjectModal = true;
      if (this.$refs.createProjectModal) {
        this.$refs.createProjectModal.clearErrorMessage();
      }
    },

    openCreateTaskModal() {
      this.showCreateTaskModal = true;
      if (this.$refs.createTaskModal) {
        this.$refs.createTaskModal.clearErrorMessage();
      }
    },

    openAddProjectMemberModal(project) {
      this.selectedProjectId = project.projectId;
      this.selectedProjectName = project.projectName;

      this.showAddProjectMemberModal = true;
      if (this.$refs.addProjectMemberModal) {
        this.$refs.addProjectMemberModal.clearErrorMessage();
      }
    },

    openAssignRoleModal() {
      this.showAssignRoleModal = true;
      if (this.$refs.assignRoleModal) {
        this.$refs.assignRoleModal.clearErrorMessage();
      }
    },

    openProgressReportsModal() {
      this.showProgressReportsModal = true;
      if (this.$refs.progressReportModal) {
        this.$refs.progressReportModal.clearErrorMessage();
      }
    },
    closeCreateTaskModal() {
      this.showCreateTaskModal = false;
      this.showError = false;
      this.errorMessage = "";
    },
    closeCreateProjectModal() {
      this.showCreateProjectModal = false;
      this.showError = false;
      this.errorMessage = "";
    },
    closeAddProjectMemberModal() {
      this.showAddProjectMemberModal = false;
      this.showError = false;
      this.errorMessage = "";
    },
    closeAssignRoleModal() {
      this.showAssignRoleModal = false;
      this.showError = false;
      this.errorMessage = "";
    },
    closeProgressReportModal() {
      this.showProgressReportsModal = false;
      this.showError = false;
      this.errorMessage = "";
    },
    openEditTalentModal(talent) {
      this.selectedTalent = talent;
      this.showEditModal = true;
    },
    closeEditTalentModal() {
      this.showEditModal = false;
    },
    async fetchUsers() {
      try {
        if (this.isAdmin) {
          const response = await apiClient.get("/web/users");
          this.users = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    },
    async fetchProjects() {
      try {
        if (this.isAdmin) {
          const response = await apiClient.get("/web/projects_all");
          this.projects = response.data.data;
        }
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    },
    async fetchProject() {
      const projectId = this.$route.params.projectId;
      try {
        if (this.projectId) {
          const response = await apiClient.get(`/web/projects/${projectId}`);
          this.project = response.data.data;
        }
      } catch (error) {
        this.showError = true;
        this.errorMessage = "Failed to fetch project details.";
        console.error("Error fetching project data:", error);
      }
    },
    async fetchProjectMembers() {
      await this.fetchProject();
    },
    async fetchProgressReports() {
      this.loading = true;
      try {
        const response = await apiClient.get(`/web/progress_reports`);

        this.reports = response.data.data || [];
        this.loading = false;
      } catch (error) {
        console.error("Error fetching progress reports:", error);
        this.loading = false;
      }
    },
    async submitEditModal(updatedTalent) {
      try {
        await apiClient.put(`/web/talents/${updatedTalent.user.user_id}`, {
          user: updatedTalent.user,
          skill_id: this.selectedSkill,
        });
        this.$refs.editTalentModal.closeModal();
      } catch (error) {
        console.error("Error updating talent:", error);
      } finally {
        this.closeEditTalentModal();
      }
    },
    handleProjectCreated(message) {
      this.showModal = false;
      this.showSuccess = true;
      this.successMessage = message;
    },
    handleMemberAdded(message) {
      this.showModal = false;
      this.showSuccess = true;
      this.successMessage = message;
    },
  },
  created() {
    this.fetchUsers();
    this.fetchProjects();
    this.fetchProgressReports();
    this.fetchProjectMembers();
  },
  mounted() {
    eventBus.on("openCreateUserModal", this.openCreateUserModal);
    eventBus.on("openCreateProjectModal", this.openCreateProjectModal);
    eventBus.on("openAddProjectMemberModal", this.openAddProjectMemberModal);
    eventBus.on("openProgressReportsModal", this.openProgressReportsModal);
    eventBus.on("openAssignRoleModal", this.openAssignRoleModal);
  },
  beforeUnmount() {
    eventBus.off("openCreateUserModal", this.openCreateUserModal);
    eventBus.off("openCreateProjectModal", this.openCreateProjectModal);
    eventBus.off("openAddProjectMemberModal", this.openAddProjectMemberModal);
    eventBus.off("openProgressReportsModal", this.openProgressReportsModal);
    eventBus.off("openAssignRoleModal", this.openAssignRoleModal);
  },
};
</script>

<style>
.app-background {
  min-height: 100vh;
  height: 100%;
}
</style>
